import { useEffect, useState } from 'react';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { PageHeader, Pager, StatusBadge } from '@/components/kit';
import { FilterSelect, ListBody, SearchBox, Toolbar, useDebounced } from '@/components/inputs';
import DataTable, { Dash, RowLink } from '@/components/admin/DataTable';
import PaymentDialog from '@/components/admin/PaymentDialog';
import { PAYMENT_KINDS, PAYMENT_KIND_STYLES } from '@/shared/constants';
import { PAYMENT_FIELDS } from '@/shared/fields';
import { PAYMENT_METHODS, RETIRED_PAYMENT_METHODS, money, paymentLabel, planName } from '@/shared/pricing';
import { dayOf, idOf, paged } from '@/shared/data';
import { formatLocalDate, DATE_FORMATS } from '@/utils/date';

const { eq, contains } = window.ApperSDK.CoreSDK;

export const route = { path: '/admin/payments', layout: 'owner', access: "['Admin_c','Staff_c'].includes(profile)" };
export const nav = { icon: 'Banknote', label: 'Payments', section: 'Store', profiles: ['Admin_c', 'Staff_c'], order: 40 };

const PAGE_SIZE = 20;
// Retired methods stay in the filter so older payments can still be found.
const METHOD_VALUES = [...PAYMENT_METHODS.map((m) => m.value), ...RETIRED_PAYMENT_METHODS];
const METHOD_LABELS = Object.fromEntries(PAYMENT_METHODS.map((m) => [m.value, m.label]));

export default function AdminPayments() {
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState('all');
  const [method, setMethod] = useState('all');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const q = useDebounced(search.trim());

  useEffect(() => {
    setPage(1);
  }, [q, kind, method]);

  const { data, loading, error, run } = useFetch(() => {
    let query = sdk.table('payments_c').select(PAYMENT_FIELDS);
    if (q) query = query.where(contains('Name', q));
    if (kind !== 'all') query = query.where(eq('kind_c', kind));
    if (method !== 'all') query = query.where(eq('method_c', method));
    return query.orderByDesc('paid_on_c').page(page, PAGE_SIZE).fetch().then(paged);
  }, [q, kind, method, page]);

  const list = data?.rows ?? [];
  const filtered = !!q || kind !== 'all' || method !== 'all';

  function clear() {
    setSearch('');
    setKind('all');
    setMethod('all');
  }

  const columns = [
    {
      label: 'Reference',
      render: (p) => (
        <RowLink to={`/admin/payments/${p.Id}`} sub={planName(p.plan_c)}>
          {p.Name}
        </RowLink>
      ),
    },
    {
      label: 'Order',
      className: 'hidden md:table-cell',
      render: (p) =>
        idOf(p.order_c) ? (
          <RowLink to={`/admin/orders/${idOf(p.order_c)}`} mono>
            {p.order_c?.Name ?? 'View order'}
          </RowLink>
        ) : (
          <Dash />
        ),
    },
    { label: 'Kind', render: (p) => <StatusBadge value={p.kind_c} styles={PAYMENT_KIND_STYLES} /> },
    { label: 'Method', className: 'hidden lg:table-cell', render: (p) => paymentLabel(p.method_c) },
    {
      label: 'Paid on',
      className: 'hidden sm:table-cell text-muted-foreground',
      render: (p) => (p.paid_on_c ? formatLocalDate(dayOf(p.paid_on_c), DATE_FORMATS.SHORT) : <Dash />),
    },
    { label: 'Amount', className: 'text-right tabular-nums', render: (p) => money(p.amount_c) },
  ];

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        eyebrow="Store"
        title="Payments"
        description="Money you have received, logged by hand. Nothing is charged from here."
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <ApperIcon name="Plus" size={16} />
            Record payment
          </Button>
        }
      />

      <Toolbar>
        <SearchBox value={search} onChange={setSearch} placeholder="Search references…" />
        <FilterSelect value={kind} onChange={setKind} values={PAYMENT_KINDS} allLabel="New and renewals" />
        <FilterSelect value={method} onChange={setMethod} values={METHOD_VALUES} labels={METHOD_LABELS} allLabel="All methods" />
      </Toolbar>

      <ListBody
        loading={loading}
        data={data}
        error={error}
        run={run}
        isEmpty={!list.length}
        empty={{
          icon: 'Banknote',
          title: filtered ? 'No payments match' : 'No payments yet',
          description: filtered ? 'Try another search or filter.' : 'Record a payment when money lands.',
          action: filtered ? clear : () => setCreateOpen(true),
          actionLabel: filtered ? 'Clear filters' : 'Record payment',
        }}
      >
        <DataTable columns={columns} rows={list} />
        <Pager page={page} total={data?.total} pageSize={PAGE_SIZE} hasMore={data?.hasMore} onPage={setPage} />
      </ListBody>

      <PaymentDialog open={createOpen} onOpenChange={setCreateOpen} onSaved={run} />
    </div>
  );
}
